import React from 'react';
import moment from 'moment';
import { FormatCurrency, constants, analyticsHelpers } from 'day8-ui';

const { SKU_TYPE, buildSkuId } = analyticsHelpers;

const { TYW_DESTINATION, CUSTOM_BOOKING_DESTINATIONS } = constants;

import {
  DEFAULT_GUEST_COUNT,
  WEEK,
  DESTINATION_PRICE_FROM_DEFAULTS,
  PRODUCTS,
  SKIPPER_PRICE_FALLBACK,
  HOST_PRICE_FALLBACK,
  PAYMENT,
  PACKAGES,
  BOOKING
} from '../constants';
import {
  getAvailableCabinsForWeekAndCategory,
  getTotalPriceFromSelectedCabins,
  isYachtsAndFriends,
  getSelectedPackageData,
  isExtraForCorrectSubject,
  getExtrasBySubject,
  getSelectedParams,
  getSelectedStandardYachtWeek,
  getCrewFromSelectedCabins
} from './week';
import { calculateRatio } from './crew';
import { capitalizeFirstLetter } from './utilities';
import { getDiscountForWeekAndSubjectFromCache, getAutomaticDiscountDataVersion } from './automaticDiscounts';
import { getBestCabinPromoDiscount } from './promoCode';
import history from '../history';
import { includedItems } from '../components/booking/WhatsIncluded';

export const MOORING_FEES_AMOUNT = 70000;

export const getHostessCost = (data, crew, forced = false) => {
  const cost = data.week.hostessCost || HOST_PRICE_FALLBACK;
  if ((!crew.hostess || data.freeHost) && forced === false) {
    return 0;
  }
  return cost / 100;
};

export const getSkipperProductVariantFromWeek = week =>
  week.mandatoryExtras?.find(extra => extra.productId === PRODUCTS.SKIPPER_ID);

export const getSkipperCost = (data, crew, forced = false) => {
  if (!crew.skipper && forced === false) {
    return 0;
  }
  const skipperPv = getSkipperProductVariantFromWeek(data.week);
  if (!skipperPv) {
    console.warn('Skipper product variant missing!');
    return 0;
  }
  const cost = skipperPv.price || SKIPPER_PRICE_FALLBACK;
  return cost / 100;
};

export const getMandatoryExtrasCost = (data, crew, isForCabin) => {
  const guests = crew.boys + crew.girls;
  const extras = data.week.mandatoryExtras?.filter(extra => isExtraForCorrectSubject(extra, isForCabin));
  let cost = 0;
  if (extras) {
    extras.forEach(extra => {
      cost += extra.addIndividually ? (extra.price * guests) : extra.price;
    });
  }
  return cost / 100;
};

export const isIsMooringIncluded = weekId => {
  // refined week ATM
  return [1309].includes(weekId);
};

export const getSelectedExtrasCost = (data, crew, isForCabin) => {
  const { extras } = getSelectedParams(history.location);
  const guests = crew.boys + crew.girls;
  const nonVariableExtras = data.week?.nonVariableExtras?.filter(extra => isExtraForCorrectSubject(extra, isForCabin) && extras.includes(extra.id));
  let cost = 0;
  if (nonVariableExtras) {
    nonVariableExtras.forEach(extra => {
      cost += extra.addIndividually ? (extra.price * guests) : extra.price;
    });
  }
  return cost / 100;
};

export const getYachtPrice = data => {
  const price = data.price;
  return price / 100;
};

export const getTotalPrice = (data, crew) => {
  const price = getYachtPrice(data, crew);
  const mandatoryHost = data.isStandard && data.needHostess; // <-- Standard Yachts with Host in the package
  let hostessPrice = getHostessCost(data, crew, mandatoryHost);
  if (hostessPrice === -1) {
    hostessPrice = 0;
  }
  const extras = getMandatoryExtrasCost(data, crew);
  const selectedExtrasCost = getSelectedExtrasCost(data, crew);
  let skipperPrice = 0;
  if (data.freeSkipper) {
    const skipperPv = getSkipperProductVariantFromWeek(data.week);
    if (skipperPv) {
      skipperPrice = skipperPv.price / 100;
    }
  }
  return price + hostessPrice + extras + selectedExtrasCost - skipperPrice;
};

export const getPricePerPerson = (data, crew) => {
  const total = getTotalPrice(data, crew);
  const guests = crew.boys + crew.girls;

  if (guests === 0) {
    return total;
  }

  return Math.ceil(total / guests);
};

/** Pre-deal total price for strike-through (deal yachts only). Returns euros or null. */
export const getDealOriginalTotalPrice = (data, crew) => {
  if (data?.isDeal !== true || typeof data.originalPrice !== 'number') {
    return null;
  }
  const originalTotal = getTotalPrice({ ...data, price: data.originalPrice }, crew);
  const currentTotal = getTotalPrice(data, crew);
  if (originalTotal <= currentTotal) {
    return null;
  }
  return originalTotal;
};

/** Pre-deal per-person price for strike-through (deal yachts only). Returns euros or null. */
export const getDealOriginalPricePerPerson = (data, crew) => {
  if (data?.isDeal !== true || typeof data.originalPrice !== 'number') {
    return null;
  }
  const guests = Math.max(crew.boys + crew.girls, 1);
  const extrasEuros = getMandatoryExtrasCost(data, crew, false);
  const totalEuros = (data.originalPrice / 100) + extrasEuros;
  const originalPerPerson = Math.floor(totalEuros / guests);
  const currentPerPerson = getPricePerPerson(data, crew);
  if (originalPerPerson <= currentPerPerson) {
    return null;
  }
  return originalPerPerson;
};

export const getDealOriginalPricePerPersonString = (data, crew) => {
  const amount = getDealOriginalPricePerPerson(data, crew);
  if (amount == null) {
    return null;
  }
  return <FormatCurrency amount={amount} baseCurrency={data.week.currency} />;
};

export const getRecommendedPrice = (data, crew) => {
  const total = getTotalPrice(data, crew);
  let recommended = data.boat.recommended;

  // TODO: if no recommended than divide by 10
  if (!recommended) {
    recommended = data.boat.capacity;
  }

  return Math.ceil(total / recommended);
};

export const getTotalPriceString = (data, crew) => {
  const total = getTotalPrice(data, crew);
  return <FormatCurrency amount={total} baseCurrency={data.week.currency} />;
};
export const getTotalStandardPriceString = (standardYachtWeek, week, crew) => {
  const total = getTotalStandardPrice(standardYachtWeek, week, crew);
  return <FormatCurrency amount={total} baseCurrency={week.currency} />;
};
export const getTotalStandardPricePerPersonString = (standardYachtWeek, week, crew) => {
  const total = getTotalStandardPricePerPerson(standardYachtWeek, week, crew);
  return <FormatCurrency amount={total} baseCurrency={week.currency} />;
};

export const getInstalment = (data, crew, percentage) => getTotalPrice(data, crew) * percentage;

export const getPricePerPersonString = (data, crew) => {
  const total = getPricePerPerson(data, crew);
  return <FormatCurrency amount={total} baseCurrency={data.week.currency} />;
};

export const getRecommendedString = (data, crew) => {
  const total = getRecommendedPrice(data, crew);
  return <FormatCurrency amount={total} baseCurrency={data.week.currency} />;
};

export const calculateYachtScore = (yacht, crewSize) => {
  const { boat } = yacht;
  const age = parseInt(moment().format('Y'), 10) + 1 - boat.year;
  let length = boat.size;

  if (boat.hullType === 'catamaran') {
    length *= 1.5;
  }
  let brandScore = 0;
  if (boat.type.indexOf('Bavaria') > -1 && boat.year < 2011) {
    brandScore = -0.1;
  } else if (boat.type.indexOf('Elan') > -1 && boat.year < 2011) {
    brandScore = -0.1;
  } else if (boat.type.indexOf('Elan') > -1) {
    brandScore = 0.1;
  } else if (boat.type.indexOf('Beneteau') > -1) {
    brandScore = 0.1;
  } else if (boat.type.indexOf('Hanse') > -1) {
    brandScore = 0.2;
  } else if (boat.type.indexOf('Dofour') > -1) {
    brandScore = 0.2;
  } else if (boat.type.indexOf('Jeanneau') > -1) {
    brandScore = 0.1;
  }
  const hasAc = boat.attributes.indexOf(1) !== -1 && boat.attributes.indexOf(2) !== -1;
  const ageScore = Math.min((10 - Math.min(age, 9)) / 10, 1) * 2;
  let spaceScore = length / crewSize / 10;
  if (crewSize > boat.cabins * 2) {
    spaceScore *= 0.1;
  }
  const priceScore = Math.min((30000 - Math.min(yacht.price / 100, 30000)) / 30000, 1) * 0.2;
  const featuredScore = yacht.featured ? 0.5 + yacht.featured / 10 : 0;
  const headScore = Math.min(1, boat.heads / crewSize);
  return ageScore + spaceScore + (hasAc ? 0.5 : 0) + headScore + priceScore + brandScore + featuredScore;
};

export const calculateDeadlineDueDate = (postBookingDays, preEventDays, beginDate) => {
  if (!preEventDays) {
    return moment().add(postBookingDays, 'days');
  }
  if (!postBookingDays) {
    return moment(beginDate).subtract(preEventDays, 'days');
  }
  const postBookingDate = moment().add(postBookingDays, 'days');
  const preEventDate = moment(beginDate).subtract(preEventDays, 'days');
  return postBookingDate < preEventDate ? postBookingDate : preEventDate;
};

export const calculateFirstDeadlinePercent = (deadlines, eventBeginDate) => {
  // Add the postBookingDays but also add a day on similar to in backend so we don't end up with too close deadlines
  const firstDeadlineCutoff = moment().add(deadlines[0].postBookingDays + 1, 'days');
  return deadlines.reduce((firstPercent, deadline) => {
    const postBookingDate = Number.isInteger(deadline.postBookingDays) ? moment().add(deadline.postBookingDays, 'days') : null;
    const preEventDate = Number.isInteger(deadline.preEventDays) ? moment(eventBeginDate).subtract(deadline.preEventDays, 'days') : null;
    let earliestDate = postBookingDate || preEventDate;
    if (postBookingDate && preEventDate) {
      earliestDate = moment.min([postBookingDate, preEventDate]);
    }
    if (earliestDate.isSameOrBefore(firstDeadlineCutoff, 'day')) {
      return firstPercent + deadline.percentage;
    }
    return firstPercent;
  }, 0);
};

export const calculatePriceRanges = (destination, yachts) => {
  const prices = { 0: [] };
  const weekCurrency = (yachts.length && yachts[0].currency) || 'EUR';

  yachts.forEach(yacht => {
    const ratingId = yacht.boat.ratingId;
    if (prices[ratingId] === undefined) {
      prices[ratingId] = [];
    }

    const guests = Math.max(DEFAULT_GUEST_COUNT, yacht.boat.capacity - 1);
    const pricePerPerson = Math.ceil((((yacht.price) / guests) + yacht.week.extra) / 100);
    prices[ratingId].push(pricePerPerson);
  });

  const average = arr => arr.reduce((p, c, i, a) => p + c / a.length, 0);

  Object.keys(prices).forEach(id => {
    const avg = Math.round(average(prices[id]));
    const min = Math.min(...prices[id]);
    prices[id] = {
      avg,
      min,
      weekCurrency,
      count: prices[id].length
    };
  });

  return prices;
};

export const calculateFromPriceOnDestination = (destination, format = false) => {
  let fixedExchangeRateOnMinPrice = null;
  let min = destination.weeks.reduce((lowest, current) => {
    if (![WEEK.SOLD_OUT_STATUS, WEEK.CLOSED_STATUS].includes(current.status) && (!current.invisible) &&
      (lowest === null || (current.fromPrices?.general !== null && current.fromPrices?.general < lowest))
    ) {
      fixedExchangeRateOnMinPrice = current.fixedExchangeRate;
      return current.fromPrices?.general;
    }
    return lowest;
  }, null);
  if (min === null) {
    min = DESTINATION_PRICE_FROM_DEFAULTS[destination.id];
  }
  // We need to track if min price is on the week with fixed currency
  // Since min prices are coming in EUR we need to exchange to display correct value
  if (fixedExchangeRateOnMinPrice) {
    min = Math.ceil((min / (fixedExchangeRateOnMinPrice / 100000)));
  }
  const isFixedRateDestination = destination.weeks.every(week => !!week.fixedExchangeRate);
  if (format && min !== null) {
    const weekCurrency = (destination.weeks.length && destination.weeks[0].currency) || 'EUR';
    return <FormatCurrency amount={min} baseCurrency={weekCurrency} showCurrency={isFixedRateDestination && weekCurrency} round />;
  }
  return min;
};

export const calculateMinCabinPricesPerPerson = weeks => {
  const minPrice = [];
  if (weeks) {
    Object.values(weeks).forEach(w => {
      Object.entries(w).forEach(([rating, cabinOrSolo]) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.entries(cabinOrSolo).forEach(([type, cw]) => {
          if (Object.values(cw).some(cabin => ![WEEK.INVISIBLE_STATUS, WEEK.SOLD_OUT_STATUS, WEEK.CLOSED_STATUS].includes(cabin.status))) {
            let price;
            price = Math.min(...Object.values(cw).map(cabin => cabin.price / cabin.persons)) ;
            if ((price && !minPrice[rating]) || price < minPrice[rating]) {
              minPrice[rating] = price;
            }
          }
        });
      });
    });
  }
  return minPrice;
};

export const calculateFromPriceOnDestinations = destinations => {
  let destinationMins = [];
  destinations.forEach(d => {
    const min = d.weeks.reduce((lowest, current) => (
      (![WEEK.SOLD_OUT_STATUS, WEEK.CLOSED_STATUS].includes(current.status) &&
        (!current.invisible) && (lowest === null || (current.fromPrices?.general !== null && current.fromPrices?.general < lowest))
      ) ? current.fromPrices?.general : lowest
    ), null);
    if (min) {
      destinationMins.push(min);
    }
  });
  // If there's absolutely no prices then we'll take from the fallbacks in constants.js
  if (!destinationMins.length) {
    destinationMins = Object.values(DESTINATION_PRICE_FROM_DEFAULTS).filter(p => !!p);
  }
  return Math.min(...destinationMins);
};

export const calculateFromPriceOnCabinDestinations = cabinDestinations => {
  const destinationCabinMins = [];
  Object.values(cabinDestinations).forEach(d => {
    const minPrices = calculateMinCabinPricesPerPerson(d);
    const min = Math.min(...Object.values(minPrices));
    if (min) {
      destinationCabinMins.push(min);
    }
  });
  return Math.min(...destinationCabinMins);
};

export const calculateMinCabinPriceOnWeekPerPerson = (week, allCabins) => {
  if (!week || !allCabins) {
    return null;
  }
  const destinationId = week.destination.id;
  const weekNumber = week.week;
  const cabins = allCabins[destinationId] && allCabins[destinationId][week.year] && allCabins[destinationId][week.year][weekNumber];
  if (!cabins) {
    return null;
  }
  const cabinWeekPrices = Object.values(cabins).reduce((a, b) => (
    b.cabin ? [...Object.values(b.cabin).reduce((c, d) => [d.price * 100 / d.persons, ...c], []), ...a] : a
  ), []);
  const soloWeekPrices = Object.values(cabins).reduce((a, b) => (
    b.solo ? [...Object.values(b.solo).reduce((c, d) => [d.price * 100, ...c], []), ...a] : a
  ), []);
  const lowestSolo = !!soloWeekPrices.length && Math.min(...soloWeekPrices);
  const lowestCabin = !!cabinWeekPrices.length && Math.min(...cabinWeekPrices);
  return lowestCabin && lowestSolo ? Math.min(lowestSolo, lowestCabin) : lowestCabin || lowestSolo;
};

export const calculateMinFromPriceOnWeek = (week, cabins) => {
  const minCabinPrice = calculateMinCabinPriceOnWeekPerPerson(week, cabins);
  const minYachtPrice = week.fromPrices?.general;
  if (!minYachtPrice && !minCabinPrice) {
    return 0;
  }
  if (minCabinPrice && minYachtPrice) {
    return Math.min(minYachtPrice * 100, minCabinPrice);
  }
  if (minYachtPrice) {
    return minYachtPrice * 100;
  }
  return minCabinPrice;
};

// base package price with all mandatory extras
export const getPackagePrice = (standardYachtWeek, week, crew) => {
  const discount = standardYachtWeek.discount || 0;
  const yachtCost = (standardYachtWeek.price - discount) / 100;
  const mandatoryExtrasCost = getMandatoryExtrasCost({ week }, crew);
  const price = yachtCost + mandatoryExtrasCost;
  return price;
};

// base package price with all mandatory extras and optional host
export const getTotalStandardPrice = (standardYachtWeek, week, crew) => {
  if (!standardYachtWeek) return 0;
  const discount = standardYachtWeek.discount || 0;
  const yachtCost = (standardYachtWeek.price - discount) / 100;
  const hostCost = crew.hostess ? getHostessCost({ week }, crew, true) : 0;
  const mandatoryExtrasCost = getMandatoryExtrasCost({ week }, crew);
  const selectedExtrasCost = getSelectedExtrasCost({ week }, crew);
  const price = yachtCost + hostCost + mandatoryExtrasCost + selectedExtrasCost;
  return price;
};

export const getLeadPackagePricePerPerson = (standardYachtWeek, week, crew) => {
  // here we need to ignore boys/girls from location and just make sure it is filled to maxCapacity for package
  const maxCrew = getPackageMaxCrew(standardYachtWeek);
  crew.total = Object.keys(crew).reduce((acc, value) => acc + crew[value], 0);
  crew.ratio = calculateRatio(crew);
  const packagePrice = getTotalStandardPrice(standardYachtWeek, week, maxCrew);
  return packagePrice / standardYachtWeek.standardYacht.maxCapacity;
};

export const getTotalStandardPricePerPerson = (standardYachtWeek, week, crew) => {
  const packagePrice = getTotalStandardPrice(standardYachtWeek, week, crew) || 0;
  return packagePrice / (crew.boys + crew.girls);
};

export const getPackageMaxCrew = standardYachtWeek => {
  const maxCapacity = standardYachtWeek.standardYacht.maxCapacity;
  const girls = Math.ceil(maxCapacity / 2);
  return {
    girls,
    boys: maxCapacity - girls,
    hostess: !!standardYachtWeek.standardYacht.host,
    skipper: 1
  };
};

export const getBookingOptionPackageCrew = standardYachtWeek => ({
  boys: standardYachtWeek.standardYacht.maxCapacity,
  girls: 0,
  hostess: standardYachtWeek.standardYacht.host ? 1 : 0,
  skipper: 1,
  total: +standardYachtWeek.standardYacht.maxCapacity + (standardYachtWeek.standardYacht.host ? 1 : 0) + 1
});

// Per-option price cache. Option objects are recreated on every buildBookingOptions, so a WeakMap
// keyed on the option auto-invalidates on rebuild (which also covers cabins/destinations changes —
// they always coincide with a rebuild). The token guards the remaining inputs: discount-data version,
// and the query string (cabin availability reads selected params from the URL). minPrice/maxPrice are
// stripped from the token — they never feed price computation — so slider commits keep the cache warm
// and the redundant filter/bounds/histogram runs per commit become near-free.
const bookingOptionPricesCache = new WeakMap();

const stripPriceParams = search =>
  (search || '').replace(/[?&](minPrice|maxPrice)=[^&]*/g, '');

// Discounted per-person cabin + yacht prices — single source of truth for filter, bounds, histogram, and cells.
export const getBookingOptionPricesPerPerson = (option, cabins, destinations, location) => {
  const token = `${getAutomaticDiscountDataVersion()}|${stripPriceParams(location && location.search)}`;
  const cached = bookingOptionPricesCache.get(option);
  if (cached && cached.token === token) {
    return cached.value;
  }
  const value = computeBookingOptionPricesPerPerson(option, cabins, destinations, location);
  bookingOptionPricesCache.set(option, { token, value });
  return value;
};

const computeBookingOptionPricesPerPerson = (option, cabins, destinations, location) => {
  const { week, category, standardYachtWeek } = option;
  if (!category) {
    return { cabin: null, yacht: null };
  }

  const packageId = category.data.package.id;
  const cabinPackageId = packageId;
  const getDiscount = getDiscountForWeekAndSubjectFromCache;
  const yachtDiscount = getDiscount(week.id, BOOKING.YACHT_SUBJECT, packageId ? +packageId : null);
  const cabinDiscount = getBestCabinPromoDiscount(getDiscount, week.id, cabinPackageId ? +cabinPackageId : null);

  let yacht = null;
  if (standardYachtWeek) {
    const packageCrew = getBookingOptionPackageCrew(standardYachtWeek);
    const discountedYacht = yachtDiscount ? getDiscountedStandardPricePerPerson(standardYachtWeek, week, packageCrew, yachtDiscount) : null;
    yacht = discountedYacht ?? getLeadPackagePricePerPerson(standardYachtWeek, week, packageCrew);
    if (!(yacht > 0)) {
      yacht = null;
    }
  }

  let cabin = null;
  if (cabinPackageId) {
    const discountedCabin = getDiscountedCabinPriceOnWeekPerPerson(
      cabins,
      destinations,
      location,
      cabinPackageId,
      week,
      cabinDiscount
    );
    cabin = discountedCabin ?? getCabinPriceOnWeekPerPerson(cabins, destinations, location, cabinPackageId, week);
    if (!(cabin > 0)) {
      cabin = null;
    }
  }

  return { cabin, yacht };
};

// Custom yacht rows use week-level from prices (same source as FestivalOptionItem custom path).
export const getCustomYachtPricePerPerson = week => {
  const price = week?.discountedFromPrices?.['general'] ?? week?.fromPrices?.['general'];
  return price != null && price > 0 ? +price : null;
};

export const getBookingOptionHistogramPrices = (option, cabins, destinations, location) => {
  if (!option.category) {
    const customPrice = getCustomYachtPricePerPerson(option.week);
    return customPrice != null ? [customPrice] : [];
  }
  const { cabin, yacht } = getBookingOptionPricesPerPerson(option, cabins, destinations, location);
  return [cabin, yacht].filter(price => price != null && price > 0);
};

export const isPriceInPerPersonRange = (price, min, max) =>
  price != null && price > 0 && +price >= +min && +price <= +max;

export const resolvePerPersonPriceFilterRange = (minPrice, maxPrice, cheapestPrice, mostExpensivePrice) => {
  if (cheapestPrice == null || mostExpensivePrice == null) {
    return {
      min: cheapestPrice,
      max: mostExpensivePrice,
      hasExplicitPriceFilter: minPrice != null || maxPrice != null
    };
  }

  let min = minPrice != null ? +minPrice : null;
  let max = maxPrice != null ? +maxPrice : null;
  const hadUrlFilter = min != null || max != null;

  // Legacy total-euro URLs (e.g. minPrice=15000) read as per-person sit far above catalogue max — fail open.
  if (min != null && min > mostExpensivePrice) {
    min = null;
  }
  if (max != null && max > mostExpensivePrice) {
    max = null;
  }

  // URL min below the cheapest real product — clamp up so the slider/filter floor matches catalogue.
  if (min != null && min < cheapestPrice) {
    min = cheapestPrice;
  }
  if (max != null && max < cheapestPrice) {
    max = cheapestPrice;
  }
  if (min != null && max != null && max < min) {
    max = min;
  }

  const hasExplicitPriceFilter = hadUrlFilter && (min != null || max != null);

  return {
    min: min != null ? min : cheapestPrice,
    max: max != null ? max : mostExpensivePrice,
    hasExplicitPriceFilter
  };
};

export const matchBookingOptionPricesToRange = ({ cabin, yacht }, { min, max }, hasExplicitPriceFilter = false) => {
  const prices = [cabin, yacht].filter(price => price != null && price > 0);
  if (!prices.length) {
    return !hasExplicitPriceFilter;
  }
  return prices.some(price => isPriceInPerPersonRange(price, min, max));
};

export const isBookingOptionInPriceRange = (option, priceRange, cabins, destinations, location, hasExplicitPriceFilter = false) => {
  if (!option.category) {
    const customPrice = getCustomYachtPricePerPerson(option.week);
    return matchBookingOptionPricesToRange({ cabin: null, yacht: customPrice }, priceRange, hasExplicitPriceFilter);
  }

  const prices = getBookingOptionPricesPerPerson(option, cabins, destinations, location);
  return matchBookingOptionPricesToRange(prices, priceRange, hasExplicitPriceFilter);
};

export const getBookingOptionsPriceBounds = (bookingOptions, cabins, destinations, location) => {
  const allPrices = bookingOptions.flatMap(option =>
    getBookingOptionHistogramPrices(option, cabins, destinations, location)
  );

  if (!allPrices.length) {
    return { cheapestPrice: null, mostExpensivePrice: null };
  }

  return {
    cheapestPrice: Math.min(...allPrices),
    mostExpensivePrice: Math.max(...allPrices)
  };
};

export const getCabinPriceOnWeekPerPerson = (cabins, destinations, location, packageId, selectedWeek) => {
  const availableCabins = getAvailableCabinsForWeekAndCategory(cabins, destinations, location, packageId, selectedWeek);
  if (!availableCabins) {
    return null;
  }
  const lowestSolo = availableCabins.solo && Math.min(...Object.values(availableCabins.solo).map(type => type.price));
  const lowestCabin = availableCabins.cabin && Math.min(...Object.values(availableCabins.cabin).map(type => type.price / type.persons));
  return lowestCabin && lowestSolo ? Math.min(lowestSolo, lowestCabin) : lowestCabin || lowestSolo;
};

/**
 * Get discounted cabin price per person for a week
 * Handles isOnFullOrder logic: when false, only cabin rental is discounted, not mandatory extras
 *
 * @param {Object} cabins - All cabins data
 * @param {Array} destinations - Destinations array
 * @param {Object} location - Location object
 * @param {number|string} packageId - Package ID
 * @param {Object} selectedWeek - The selected week object
 * @param {Object|null} discount - Resolved discount for cabin + package (from getDiscountForWeekAndSubject)
 * @returns {number|null} Discounted cabin price per person, or null if no discount applies
 */
export const getDiscountedCabinPriceOnWeekPerPerson = (cabins, destinations, location, packageId, selectedWeek, discount) => {
  if (!selectedWeek || !discount || (discount?.discountPercentage ?? 0) === 0) {
    return null;
  }
  const discountPercentage = discount.discountPercentage || 0;

  // Get the base cabin price per person (this includes mandatory extras)
  const baseCabinPricePerPerson = getCabinPriceOnWeekPerPerson(cabins, destinations, location, packageId, selectedWeek);
  if (!baseCabinPricePerPerson) {
    return null;
  }

  // If discount applies to full order, discount everything
  if (discount.isOnFullOrder) {
    return baseCabinPricePerPerson * (1 - discountPercentage / 100);
  }

  // When isOnFullOrder is false, only discount the cabin rental portion, not mandatory extras
  // Calculate mandatory extras per person to subtract from total
  // Use a representative crew (1 person) to get per-person mandatory extras cost
  const singlePersonCrew = { boys: 1, girls: 0 };
  const mandatoryExtrasPerPerson = getMandatoryExtrasCost({ week: selectedWeek }, singlePersonCrew, true);

  // Cabin rental per person = total price per person - mandatory extras per person
  const cabinRentalPerPerson = baseCabinPricePerPerson - mandatoryExtrasPerPerson;

  // Discount only the cabin rental portion
  const discountedCabinRentalPerPerson = cabinRentalPerPerson * (1 - discountPercentage / 100);

  // Return discounted cabin rental + undiscounted mandatory extras
  return discountedCabinRentalPerPerson + mandatoryExtrasPerPerson;
};

const getIncludedMandatoryExtraProducts = destinationId => {
  const includedMandatoryExtraProducts = [PRODUCTS.DRINKS_PACKAGE];
  if ([TYW_DESTINATION.YACHTNIK_CROATIA, TYW_DESTINATION.YACHTNIK_MART_ST_LUCIA].includes(destinationId)) {
    includedMandatoryExtraProducts.push(PRODUCTS.MOORING_FEES);
  }
  return includedMandatoryExtraProducts;
};

export const getStandardYachtWeekCosts = (standardYachtWeek, week, crew) => {
  const hideStaffItems = CUSTOM_BOOKING_DESTINATIONS.includes(week.destination.id);
  const includedMandatoryExtraProducts = getIncludedMandatoryExtraProducts(week?.destination.id);
  const guests = crew.boys + crew.girls;
  const skipperCost = getSkipperCost({ week }, crew, true);
  const mandatoryExtras = getExtrasBySubject(week?.mandatoryExtras);
  const includedMandatoryExtraCostItems = [];
  if (mandatoryExtras) {
    includedMandatoryExtraProducts.forEach(product => {
      const extra = mandatoryExtras.find(extra => product === extra.productId);
      if (extra) {
        includedMandatoryExtraCostItems.push({
          label: `${extra.name}${extra.addIndividually ? ` x ${guests}` : null}`,
          price: extra.addIndividually ? (extra.price * guests) / 100 : extra.price / 100,
          currency: week.currency,
          description: extra.description,
          readMoreLink: extra.readMoreLink
        });
      }
    });
  }
  const includedMandatoryExtraCostItemsPriceSum = includedMandatoryExtraCostItems.reduce((acc, item) => item && item.price && (acc + item.price), 0);
  // have to include skipper cost here because we need to hide it from breakdown...
  const basePackagePrice = getPackagePrice(standardYachtWeek, week, crew) - getMandatoryExtrasCost({ week }, crew) + skipperCost + includedMandatoryExtraCostItemsPriceSum;
  const costs = [
    {
      label: `${capitalizeFirstLetter(standardYachtWeek.standardYacht.package.name)} package`,
      currency: week.currency,
      price: basePackagePrice
    }
  ];
  const hostessPrice = crew && getHostessCost({ week }, crew, true);
  if (mandatoryExtras) {
    mandatoryExtras
      .filter(extra => ![...includedMandatoryExtraProducts, PRODUCTS.SKIPPER_ID, PRODUCTS.HALF_BOARD_CLASSIC, PRODUCTS.HALF_BOARD_LUXURY, PRODUCTS.HALF_BOARD_LUXURY_PLUS, PRODUCTS.HALF_BOARD_PREMIUM, PRODUCTS.HOSTESS_ID, PRODUCTS.WRISTBAND_ID].includes(extra.productId))
      .forEach(extra => costs.push({
        label: `${extra.name}${extra.addIndividually ? ` x ${guests}` : null}`,
        price: extra.addIndividually ? (extra.price * guests) / 100 : extra.price / 100,
        currency: week.currency,
        description: extra.description,
        readMoreLink: extra.readMoreLink
      }));
  }
  if (crew.hostess && hostessPrice && !hideStaffItems) costs.push({ label: isYachtsAndFriends(week) ? 'Chef' : 'Host', price: hostessPrice, currency: week.currency });
  if (!hideStaffItems) costs.push({ label: 'Skipper', included: true });
  const isGreeceBootcamp = week?.destination?.id === TYW_DESTINATION.GREECE_BOOTCAMP;
  if (isGreeceBootcamp) {
    costs.push({ label: 'Fuel & Mooring', included: true });
  }
  if (includedMandatoryExtraCostItems && includedMandatoryExtraCostItems.length) includedMandatoryExtraCostItems.forEach(item => costs.push({ label: item.label, included: true }));
  const isStandardYacht = true;
  const excludeStaffItems = true;
  const whatsIncludedItems = includedItems(isStandardYacht, standardYachtWeek.standardYacht?.package, week.destination.id, null, null, week, isYachtsAndFriends(week), standardYachtWeek, excludeStaffItems);
  whatsIncludedItems.forEach(item => costs.push({ label: item, included: true }));
  // Adding deduping to avoid duplicate items
  const seen = new Set();
  const dedupedCosts = [];
  costs.forEach(item => {
    if (!seen.has(item.label.toLowerCase())) {
      dedupedCosts.push(item);
      seen.add(item.label.toLowerCase());
    }
  });
  return dedupedCosts;
};

export const getCabinCosts = (allCabins, destinations, location, week, cabinCrew) => {
  const includedMandatoryExtraProducts = getIncludedMandatoryExtraProducts(week?.destination.id);
  const totalPrice = getTotalPriceFromSelectedCabins(allCabins.data, destinations, location);
  const extrasCost = getMandatoryExtrasCost({ week }, cabinCrew, true);
  const guests = cabinCrew.boys + cabinCrew.girls;
  const mandatoryExtras = getExtrasBySubject(week?.mandatoryExtras, true);
  const packageData = getSelectedPackageData(destinations, location);
  const selectedExtrasCost = getSelectedExtrasCost({ week }, cabinCrew, true);
  const includedMandatoryExtraCostItems = [];
  if (mandatoryExtras) {
    includedMandatoryExtraProducts.forEach(product => {
      const extra = mandatoryExtras.find(extra => product === extra.productId);
      if (extra) {
        includedMandatoryExtraCostItems.push({
          label: `${extra.name}${extra.addIndividually ? ` x ${guests}` : null}`,
          price: extra.addIndividually ? (extra.price * guests) / 100 : extra.price / 100,
          currency: week.currency,
          description: extra.description,
          readMoreLink: extra.readMoreLink
        });
      }
    });
  }
  const includedMandatoryExtraCostItemsPriceSum = includedMandatoryExtraCostItems.reduce((acc, item) => item && item.price && (acc + item.price), 0);
  const totalCabinPrice = totalPrice - extrasCost - selectedExtrasCost + includedMandatoryExtraCostItemsPriceSum;
  const costs = [];
  if (totalCabinPrice) {
    costs.push({ label: 'Cabin rental', price: totalCabinPrice, currency: week && week.currency });
  }
  if (mandatoryExtras) {
    mandatoryExtras.forEach(extra => costs.push({
      label: `${extra.name}${extra.addIndividually ? ` x ${guests}` : null}`,
      price: extra.addIndividually ? (extra.price * guests) / 100 : extra.price / 100,
      currency: week && week.currency,
      description: extra.description,
      readMoreLink: extra.readMoreLink
    }));
  }
  costs.push({ label: 'Skipper', included: true });
  // what if we added included costs field on cabins_week???
  // they could add comma separated names and it will appear as included - per cabin/destination etc...
  if (packageData.id !== PACKAGES.CLASSIC_MONO) {
    costs.push({ label: getHostProductName(week), included: true });
    costs.push({ label: 'Half Board', included: true });
  }
  // We need to add Mooring fees item here manually because it's yachtOnlyExtra therefore won't come in mandatoryExtras
  if (isIsMooringIncluded(week?.id)) {
    costs.push({ label: 'Fuel & Mooring', included: true });
  }
  if (includedMandatoryExtraCostItems && includedMandatoryExtraCostItems.length) includedMandatoryExtraCostItems.forEach(item => costs.push({ label: item.label, included: true }));
  const isStandardYacht = true;
  const selectedStandardYachtWeek = getSelectedStandardYachtWeek(destinations, location);
  const excludeStaffItems = true;
  const whatsIncludedItems = includedItems(isStandardYacht, packageData, week.destination.id, cabinCrew.cabins, cabinCrew.solos, week, isYachtsAndFriends(week), selectedStandardYachtWeek, excludeStaffItems);
  whatsIncludedItems.forEach(item => costs.push({ label: item, included: true }));
  // Adding deduping to avoid duplicate items
  const seen = new Set();
  const dedupedCosts = [];
  costs.forEach(item => {
    if (!seen.has(item.label.toLowerCase())) {
      dedupedCosts.push(item);
      seen.add(item.label.toLowerCase());
    }
  });
  return dedupedCosts;
};

export const getHostProductName = week => isYachtsAndFriends(week) ? 'Chef' : 'Host';

// ---------------------------------------------------------------------------
// Analytics: itemised view_item lists that mirror the begin_checkout items
// (analyticsHelpers.buildOrderItems) as closely as is possible pre-booking.
// item_id parity with begin_checkout:
//   - yacht main line -> boatWeek id (custom) / standardYachtWeek id (package)
//   - cabins          -> one item per cabin, item_id = cabinWeek id
//   - extras          -> PRODUCTS product id (the productVariant id only exists once an order is created)
// Every item also carries sku_id: the same id prefixed with its type (see SKU_TYPE in day8-ui
// analyticsHelpers), which is what the Meta product feed matches on and how the pixel recognises
// extras. item_id stays a bare integer so GA4 item reporting is unaffected.
// Prices are per-line in major currency units (consistent with the view_item `value`/totalPrice).
// NB: best-effort - some "included" mandatory extras (e.g. drinks) that the sidebar folds into the
// main line are listed here as their own priced item; the event `value` stays the authoritative total.
// ---------------------------------------------------------------------------
const buildExtraViewItems = (week, crew, isForCabin, context) => {
  const guests = (crew?.boys || 0) + (crew?.girls || 0);
  const toItem = extra => ({
    item_id: extra.productId,
    sku_id: buildSkuId(SKU_TYPE.EXTRA, extra.productId),
    item_name: extra.name,
    ...context,
    // Full line price (per-person extras x guests), quantity 1 - stable per item_id, never per-person.
    price: (extra.addIndividually ? extra.price * guests : extra.price) / 100,
    quantity: 1
  });
  // mandatory extras (skipper & host are handled explicitly by the callers).
  // NOT for cabins: the cabin line price already contains them. The cabins API adds the week's
  // mandatory extras into the price it serves (YachtListManager::refreshCabinData -> (cabinWeek price
  // + mandatory extras) / 100), so itemising them again here counted them twice and made
  // sum(items) exceed the event's own `value`. NB this is why the cabin items diverge from
  // buildOrderItems, where the booked cabin line is the BARE cabin-week price and the mandatory
  // extras are separate order lines.
  const mandatory = isForCabin ? [] : (week?.mandatoryExtras || []).filter(extra =>
    isExtraForCorrectSubject(extra, isForCabin)
    && extra.productId !== PRODUCTS.SKIPPER_ID
    && extra.productId !== PRODUCTS.HOSTESS_ID);
  // optional extras the user has selected
  const { extras: selectedIds = [] } = getSelectedParams(history.location);
  const selected = (week?.nonVariableExtras || []).filter(extra =>
    isExtraForCorrectSubject(extra, isForCabin) && selectedIds.includes(extra.id));
  return [...mandatory, ...selected].map(toItem);
};

const withIndexes = items => items.map((item, index) => ({ ...item, index }));

// Custom yacht (YachtCard): main line = boatWeek id, then charged skipper/host + extras
export const getYachtViewItems = (yacht, crew) => {
  if (!yacht) return [];
  const week = yacht.week;
  const context = { item_category: week?.week, item_category2: 'Custom Yacht', item_category3: 'yacht' };
  const items = [{
    item_id: yacht.id,
    sku_id: buildSkuId(SKU_TYPE.BOAT_WEEK, yacht.id),
    item_name: week?.destination?.name,
    ...context,
    price: getYachtPrice(yacht),
    quantity: 1
  }];
  const skipperCost = getSkipperCost(yacht, crew, false);
  if (skipperCost) items.push({ item_id: PRODUCTS.SKIPPER_ID, sku_id: buildSkuId(SKU_TYPE.EXTRA, PRODUCTS.SKIPPER_ID), item_name: 'Skipper', ...context, price: skipperCost, quantity: 1 });
  const hostCost = getHostessCost(yacht, crew, false);
  if (hostCost) items.push({ item_id: PRODUCTS.HOSTESS_ID, sku_id: buildSkuId(SKU_TYPE.EXTRA, PRODUCTS.HOSTESS_ID), item_name: getHostProductName(week), ...context, price: hostCost, quantity: 1 });
  return withIndexes([...items, ...buildExtraViewItems(week, crew, false, context)]);
};

// Package / standard yacht (ProductConfirm): main line = standardYachtWeek id (skipper folded into the
// package price, as standard yachts include it), then charged host + extras
export const getStandardYachtViewItems = (standardYachtWeek, week, crew) => {
  if (!standardYachtWeek || !week) return [];
  const packageName = standardYachtWeek.standardYacht?.package?.name || null;
  const context = { item_category: week?.week, item_category2: packageName, item_category3: 'yacht' };
  const discount = standardYachtWeek.discount || 0;
  const items = [{
    item_id: standardYachtWeek.id,
    sku_id: buildSkuId(SKU_TYPE.STANDARD_YACHT_WEEK, standardYachtWeek.id),
    item_name: week?.destination?.name,
    ...context,
    price: (standardYachtWeek.price - discount) / 100,
    quantity: 1
  }];
  // Skipper is a SEPARATE line item on the booking order, so itemise it here too (it used to be folded
  // into the package price, which mismatched add_to_cart / begin_checkout / purchase).
  const skipperCost = getSkipperCost({ week }, crew, true);
  if (skipperCost) items.push({ item_id: PRODUCTS.SKIPPER_ID, sku_id: buildSkuId(SKU_TYPE.EXTRA, PRODUCTS.SKIPPER_ID), item_name: 'Skipper', ...context, price: skipperCost, quantity: 1 });
  const hostCost = crew?.hostess ? getHostessCost({ week }, crew, true) : 0;
  if (hostCost) items.push({ item_id: PRODUCTS.HOSTESS_ID, sku_id: buildSkuId(SKU_TYPE.EXTRA, PRODUCTS.HOSTESS_ID), item_name: getHostProductName(week), ...context, price: hostCost, quantity: 1 });
  return withIndexes([...items, ...buildExtraViewItems(week, crew, false, context)]);
};

// Cabins (CabinConfirm): one item per selected cabin (item_id = cabinWeek id), then cabin extras
export const getCabinViewItems = (selectedCabins, week, crew, packageName = null) => {
  if (!selectedCabins?.length) return [];
  const context = { item_category: week?.week, item_category2: packageName, item_category3: 'cabin' };
  const cabinItems = selectedCabins.map(cabin => ({
    item_id: cabin.id,
    sku_id: buildSkuId(SKU_TYPE.CABIN_WEEK, cabin.id),
    item_name: week?.destination?.name,
    ...context,
    price: cabin.price,
    quantity: 1
  }));
  return withIndexes([...cabinItems, ...buildExtraViewItems(week, crew, true, context)]);
};

// ---------------------------------------------------------------------------
// Analytics: view_item_list items built from the list actually shown on the funnel.
// No prices here on purpose (the list shows aggregate "from" prices that wouldn't match the
// itemised line prices downstream); item_ids match the other events so GA4 can tie them together.
// ---------------------------------------------------------------------------

// Yacht list (/browse/yachts + YnF /charter): each displayed boatWeek -> item_id = boatWeek id.
// startIndex keeps the GA4 list index absolute when firing successive infinite-scroll batches.
export const getYachtListViewItems = (yachts = [], startIndex = 0) =>
  yachts.map((yacht, i) => ({
    item_id: yacht.id,
    sku_id: buildSkuId(SKU_TYPE.BOAT_WEEK, yacht.id),
    item_name: yacht.boat?.type || yacht.week?.destination?.name,
    item_category: yacht.week?.week,
    item_category2: yacht.package?.name || 'Custom Yacht',
    item_category3: 'yacht',
    index: startIndex + i,
    price: null,
    quantity: 1
  }));

// Calendar / booking-options (/browse first step + YnF equivalent): each displayed option ->
// the package yacht (standardYachtWeek id) + every available cabin for that week+package (cabinWeek id).
// Deduped by item_id so a cabin/package shared across options appears once.
export const getFunnelOptionsViewItems = (bookingOptions = [], cabinsData, destinations, location) => {
  const byId = new Map();
  bookingOptions.forEach(option => {
    const week = option.week;
    const packageId = option.category?.data?.package?.id;
    const packageName = option.category?.data?.package?.name || 'Custom Yacht';
    const context = { item_category: week?.week, item_category2: packageName };
    const standardYachtWeekId = option.standardYachtWeek?.id;
    if (standardYachtWeekId && !byId.has(`yacht_${standardYachtWeekId}`)) {
      byId.set(`yacht_${standardYachtWeekId}`, {
        item_id: standardYachtWeekId,
        sku_id: buildSkuId(SKU_TYPE.STANDARD_YACHT_WEEK, standardYachtWeekId),
        item_name: week?.destination?.name,
        ...context,
        item_category3: 'yacht',
        price: null,
        quantity: 1
      });
    }
    if (packageId && cabinsData) {
      const available = getAvailableCabinsForWeekAndCategory(cabinsData, destinations, location, packageId, week);
      const cabinObjects = [
        ...(available?.cabin ? Object.values(available.cabin) : []),
        ...(available?.solo ? Object.values(available.solo) : [])
      ];
      cabinObjects.forEach(cabin => {
        if (cabin?.id && !byId.has(`cabin_${cabin.id}`)) {
          byId.set(`cabin_${cabin.id}`, {
            item_id: cabin.id,
            sku_id: buildSkuId(SKU_TYPE.CABIN_WEEK, cabin.id),
            item_name: week?.destination?.name,
            ...context,
            item_category3: 'cabin',
            price: null,
            quantity: 1
          });
        }
      });
    }
  });
  return withIndexes([...byId.values()]);
};

export const getCreditNotesForUser = (payments, userId) => {
  const creditNotes = payments.reduce((acc, payment) => {
    if (payment.profile.id === userId && payment.status === PAYMENT.PAID_STATUS) {
      return acc + payment.amountRegistered;
    }
    return acc;
  }, 0);
  return creditNotes < 1 ? 0 : creditNotes;
};

export const getCardChargesExist = () => {
  try {
    const cardCharges = window?.localStorage?.creditCardCharges ? JSON.parse(window.localStorage.creditCardCharges) : {};
    return Object.values(cardCharges).some(val => val > 0);
  } catch (e) {
    console.error(e);
    return false;
  }
};

/**
 * Calculate discounted total price for yacht bookings
 * @param {Object} data - Yacht data object
 * @param {Object} crew - Crew object with boys, girls, skipper, hostess
 * @param {Object|null} discount - Resolved discount for yacht (from getDiscountForWeekAndSubject)
 * @param {Object} week - Week object with id
 * @returns {number|null} Discounted price or null if no discount
 */
export const getDiscountedTotalPrice = (data, crew, discount, week) => {
  if (!discount || !week || (discount.discountPercentage ?? 0) === 0) {
    return null;
  }
  if (!discount.isActiveOnNonStandardBookings) {
    return null;
  }
  const discountPercentage = discount.discountPercentage || 0;

  const originalPrice = getTotalPrice(data, crew);

  if (discount.isOnFullOrder) {
    // Full order discount - applies to everything
    return originalPrice * (1 - discountPercentage / 100);
  } else {
    // Yacht + Skipper discount (not other extras, not host)
    const yachtPrice = getYachtPrice(data);
    const skipperPrice = getSkipperCost(data, crew, true);
    const yachtAndSkipperPrice = yachtPrice + skipperPrice;
    const discountedYachtAndSkipper = yachtAndSkipperPrice * (1 - discountPercentage / 100);

    // Get mandatory extras cost (which includes skipper)
    const allMandatoryExtras = getMandatoryExtrasCost(data, crew);
    // Subtract skipper from mandatory extras since we're discounting it separately
    const mandatoryExtrasWithoutSkipper = allMandatoryExtras - skipperPrice;

    const selectedExtrasCost = getSelectedExtrasCost(data, crew);
    const mandatoryHost = data.isStandard && data.needHostess;
    const hostessPrice = getHostessCost(data, crew, mandatoryHost);

    // Handle freeSkipper: if skipper is free, we need to subtract it from the total
    // (same logic as getTotalPrice - skipper is included in mandatoryExtras but then subtracted)
    let freeSkipperDeduction = 0;
    if (data.freeSkipper) {
      const skipperPv = getSkipperProductVariantFromWeek(data.week);
      if (skipperPv) {
        freeSkipperDeduction = skipperPv.price / 100;
      }
    }

    return discountedYachtAndSkipper + mandatoryExtrasWithoutSkipper + selectedExtrasCost + hostessPrice - freeSkipperDeduction;
  }
};

/**
 * Calculate discounted price per person for yacht bookings
 * @param {Object} data - Yacht data object
 * @param {Object} crew - Crew object with boys, girls, skipper, hostess
 * @param {Object|null} discount - Resolved discount (from getDiscountForWeekAndSubject)
 * @param {Object} week - Week object with id
 * @returns {number|null} Discounted price per person or null if no discount
 */
export const getDiscountedPricePerPerson = (data, crew, discountData, week) => {
  const discountedTotal = getDiscountedTotalPrice(data, crew, discountData, week);
  if (!discountedTotal) {
    return null;
  }
  const guests = crew.boys + crew.girls;
  return guests > 0 ? Math.ceil(discountedTotal / guests) : discountedTotal;
};

/**
 * Calculate discounted total price for standard yacht weeks
 * @param {Object} standardYachtWeek - Standard yacht week object
 * @param {Object} week - Week object with id
 * @param {Object} crew - Crew object
 * @param {Object|null} discount - Resolved discount for yacht + package (from getDiscountForWeekAndSubject)
 * @returns {number|null} Discounted price or null if no discount
 */
export const getDiscountedStandardPrice = (standardYachtWeek, week, crew, discount) => {
  if (!discount || !week || !standardYachtWeek || (discount.discountPercentage ?? 0) === 0) {
    return null;
  }
  const discountPercentage = discount.discountPercentage || 0;

  const originalPrice = getTotalStandardPrice(standardYachtWeek, week, crew);
  if (discount.isOnFullOrder) {
    // Full order discount
    return originalPrice * (1 - discountPercentage / 100);
  } else {
    // Yacht + Skipper discount (not other extras, not host)
    const existingDiscount = standardYachtWeek.discount || 0;
    const yachtCost = (standardYachtWeek.price - existingDiscount) / 100;
    const skipperCost = getSkipperCost({ week }, crew, true);
    const yachtAndSkipperCost = yachtCost + skipperCost;
    const discountedYachtAndSkipper = yachtAndSkipperCost * (1 - discountPercentage / 100);

    // Get mandatory extras cost (which includes skipper)
    const allMandatoryExtras = getMandatoryExtrasCost({ week }, crew);
    // Subtract skipper from mandatory extras since we're discounting it separately
    const mandatoryExtrasWithoutSkipper = allMandatoryExtras - skipperCost;

    // Add back all the non-discounted items
    const hostCost = crew.hostess ? getHostessCost({ week }, crew, true) : 0;
    const selectedExtrasCost = getSelectedExtrasCost({ week }, crew);
    return discountedYachtAndSkipper + mandatoryExtrasWithoutSkipper + hostCost + selectedExtrasCost;
  }
};

/**
 * Calculate discounted price per person for standard yacht weeks
 * @param {Object} standardYachtWeek - Standard yacht week object
 * @param {Object} week - Week object with id
 * @param {Object} crew - Crew object
 * @param {Object|null} discount - Resolved discount (from getDiscountForWeekAndSubject)
 * @returns {number|null} Discounted price per person or null if no discount
 */
export const getDiscountedStandardPricePerPerson = (standardYachtWeek, week, crew, discountData) => {
  const discountedTotal = getDiscountedStandardPrice(standardYachtWeek, week, crew, discountData);
  if (!discountedTotal) {
    return null;
  }
  const guests = crew ? crew.boys + crew.girls : standardYachtWeek.standardYacht.maxCapacity;
  return guests > 0 ? Math.ceil(discountedTotal / guests) : discountedTotal;
};

/**
 * Get formatted discounted total price string
 * @param {Object} data - Yacht data object
 * @param {Object} crew - Crew object
 * @param {Object|null} discount - Resolved discount (from getDiscountForWeekAndSubject)
 * @param {Object} week - Week object
 * @returns {React.Element|null} Formatted currency component or null
 */
export const getDiscountedTotalPriceString = (data, crew, discountData, week) => {
  const discountedTotal = getDiscountedTotalPrice(data, crew, discountData, week);
  if (!discountedTotal) {
    return null;
  }
  return <FormatCurrency amount={discountedTotal} baseCurrency={week.currency} />;
};

/**
 * Get formatted discounted price per person string
 * @param {Object} data - Yacht data object
 * @param {Object} crew - Crew object
 * @param {Object|null} discount - Resolved discount (from getDiscountForWeekAndSubject)
 * @param {Object} week - Week object
 * @returns {React.Element|null} Formatted currency component or null
 */
export const getDiscountedPricePerPersonString = (data, crew, discountData, week) => {
  const discountedPerPerson = getDiscountedPricePerPerson(data, crew, discountData, week);
  if (!discountedPerPerson) {
    return null;
  }
  return <FormatCurrency amount={discountedPerPerson} baseCurrency={week.currency} />;
};

/**
 * Calculate discounted total price for cabin bookings
 * @param {Object} allCabins - All cabins data
 * @param {Array} destinations - Destinations array
 * @param {Object} location - Location object
 * @param {Object|null} discount - Resolved discount for cabin + package (from getDiscountForWeekAndSubject)
 * @param {Object} week - Week object with id
 * @param {Object} selectedPackageData - Selected package data
 * @returns {number|null} Discounted price or null if no discount
 */
export const getDiscountedCabinPrice = (
  allCabins = null,
  destinations = null,
  location = null,
  discount,
  week,
  selectedPackageData
) => {
  if (!discount || !week || (discount?.discountPercentage ?? 0) === 0) {
    return null;
  }
  const discountPercentage = discount.discountPercentage || 0;
  const selectedCrew = getCrewFromSelectedCabins(allCabins, destinations, location);
  const originalTotalPrice = getTotalPriceFromSelectedCabins(allCabins, destinations, location, week, selectedPackageData.id);
  const mandatoryExtrasCost = getMandatoryExtrasCost({ week }, selectedCrew, true);
  const selectedExtrasCost = getSelectedExtrasCost({ week }, selectedCrew, true);
  if (discountPercentage === 0) {
    return null;
  }

  if (discount.isOnFullOrder) {
    return originalTotalPrice * (1 - discountPercentage / 100);
  } else {
    // Cabin rental price only discount (excluding mandatory extras)
    const discountedCabinPrice = (originalTotalPrice - mandatoryExtrasCost - selectedExtrasCost) * (1 - discountPercentage / 100);
    // Add back mandatory extras (not discounted)
    return discountedCabinPrice + mandatoryExtrasCost + selectedExtrasCost;
  }
};
